import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import classNames from "../../../../utils/classNames";
import FormattingToolbar from "../../../molecules/Composer/FormattingToolbar";
import RichTextInput from "../../../molecules/Composer/RichTextInput";
import FooterActions from "../../../molecules/Composer/FooterActions";
import RichTextRenderer from "../../../molecules/Composer/RichTextRenderer";
import useComposerFormatting from "../../../../hooks/useComposerFormatting";
import useFocusTrap from "../../../../hooks/useFocusTrap";
import useStompTopic from "../../../../hooks/useStompTopic";
import chatService from "../../../../services/chatService";
import { computeCanSend, getEditorLiveHtml } from "../../../molecules/Composer/composerUtils";
import "../../../molecules/Composer/composer-v2.scss";
import CommentRow from "../../../molecules/CommentRow";

export default function MessageCommentDrawer({
  isOpen,
  onClose,
  message = undefined,
  onSubmit,
  initialDraft = "",
  messageContentRenderer,
  stompClient = undefined,
  isSocketConnected = undefined,
  resolveUserName = undefined,
  variant = "overlay",
  showHeader = true,
}) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const editorRef = useRef(null);
  const [draftHtml, setDraftHtml] = useState(initialDraft);
  const { formatState, recomputeFormatState, applyWrapFormatting, applyCodeFormatting, applyQuoteFormatting, applyListFormatting, applyLinkFormatting } = useComposerFormatting(editorRef);
  const authUser = useSelector((state) => state.auth && state.auth.user);
  const currentUserId = authUser?.id ? String(authUser.id) : null;

  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);

  useFocusTrap({
    active: variant !== "inline" && isOpen,
    containerRef: panelRef,
  });

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => editorRef.current?.focus?.(), 0);
    }
  }, [isOpen]);

  const canSend = useMemo(() => computeCanSend({ editorRef, draftHtml, isSending: false }), [draftHtml, editorRef]);

  // Manage object URLs for inline image previews
  const blobUrlsRef = useRef(new Set());
  useEffect(() => () => {
    try { blobUrlsRef.current.forEach((u) => { try { URL.revokeObjectURL(u); } catch (_) {} }); blobUrlsRef.current.clear(); } catch (_) {}
  }, []);

  const handleSelectImages = useCallback((files) => {
    if (!Array.isArray(files) || !files.length) return;
    files.forEach((file) => {
      try {
        const url = URL.createObjectURL(file);
        blobUrlsRef.current.add(url);
        const safeAlt = (file?.name || 'image').replace(/["<>]/g, '').slice(0, 200);
        const html = `<img src="${url}" alt="${safeAlt}" loading="lazy">`;
        editorRef.current?.exec?.('html', `${html}<br>`);
      } catch (_) { /* ignore */ }
    });
  }, []);

  // define after fetchPage; declared below

  // key handler defined after submit below

  const fetchPage = useCallback(async (nextPage = 0) => {
    if (!message?.id) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await chatService.listComments(message.id, nextPage, size);
      const items = Array.isArray(resp?.comments) ? resp.comments : (Array.isArray(resp?.items) ? resp.items : []);
      const count = Number.isFinite(resp?.total) ? resp.total : items.length;
      if (nextPage === 0) {
        setComments(items);
      } else {
        setComments((prev) => [...prev, ...items]);
      }
      setTotal(count);
      setPage(nextPage);
    } catch (e) {
      setError(t("chatPage.comments.errors.load", "Failed to load comments"));
    } finally {
      setLoading(false);
    }
  }, [message?.id, size, t]);

  useEffect(() => {
    if (isOpen && message?.id) {
      setComments([]);
      setTotal(0);
      setPage(0);
      fetchPage(0);
    }
  }, [isOpen, message?.id, fetchPage]);

  const handleSubmitComment = useCallback(async (html) => {
    try {
      const trimmed = String(html || "").trim();
      if (!trimmed) return;
      await chatService.addComment(message.id, trimmed);
      setDraftHtml("");
      await fetchPage(0);
      onSubmit?.({ type: 'add', messageId: message?.id });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to add comment', e);
    }
  }, [message?.id, fetchPage, onSubmit]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      if (!canSend) return;
      const html = getEditorLiveHtml(editorRef) || draftHtml || "";
      void handleSubmitComment(html);
    }
  }, [canSend, draftHtml, onClose, handleSubmitComment]);

  useEffect(() => {
    if (variant === "inline" || !isOpen) {
      return undefined;
    }

    const handleDocumentKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }
      event.preventDefault();
      onClose?.();
    };

    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [isOpen, onClose, variant]);

  // send handler is invoked via FooterActions below

  const handleEdit = useCallback(async (commentId, nextContent) => {
    if (!commentId) return;
    try {
      const trimmed = String(nextContent || "").trim();
      if (!trimmed) return;
      await chatService.editComment(message.id, commentId, trimmed);
      fetchPage(page);
      setEditingCommentId(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to edit comment', e);
    }
  }, [message?.id, page, fetchPage]);

  const handleDelete = useCallback(async (commentId) => {
    if (!commentId) return;
    try {
      await chatService.deleteComment(message.id, commentId);
      setComments((prev) => prev.filter((c) => String(c.id) !== String(commentId)));
      setTotal((t) => Math.max(0, t - 1));
      onSubmit?.({ type: 'delete', messageId: message?.id, commentId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete comment', e);
    }
  }, [message?.id, onSubmit]);

  // Subscribe to real-time comment events for the message's chat stream
  const canSubscribe = Boolean(
    isOpen
    && stompClient
    && message?.chatId
    && (isSocketConnected ?? stompClient?.connected)
  );

  useStompTopic({
    client: stompClient,
    destination: message?.chatId ? `/topic/${message.chatId}` : null,
    enabled: canSubscribe,
    debugLabel: 'comments',
    onMessage: useCallback((frame) => {
      try {
        const payload = JSON.parse(frame.body);
        if (!payload || payload.messageId !== message?.id) return;
        switch (payload.type) {
          case 'COMMENT_ADD': {
            const added = payload.comment;
            if (!added) break;
            setComments((prev) => {
              const exists = prev.some((c) => String(c.id) === String(added.id));
              return exists ? prev : [...prev, added];
            });
            setTotal((t) => (Number.isFinite(t) ? t + 1 : 1));
            break;
          }
          case 'COMMENT_EDIT': {
            const updated = payload.comment;
            if (!updated) break;
            setComments((prev) => prev.map((c) => String(c.id) === String(updated.id) ? { ...c, ...updated } : c));
            break;
          }
          case 'COMMENT_DELETE': {
            const id = payload.commentId;
            if (!id) break;
            setComments((prev) => prev.filter((c) => String(c.id) !== String(id)));
            setTotal((t) => Math.max(0, t - 1));
            break;
          }
          default:
            break;
        }
      } catch (_) {
        // ignore parse errors
      }
    }, [message?.id])
  });

  const renderDrawerContent = () => (
    <div className="side-drawer__content" aria-label={variant === "inline" ? t("chatPage.comments.dialogLabel", "Message comments") : undefined}>
      <div className="side-drawer__message-preview">
        <div className="side-drawer__message-author">{(typeof resolveUserName === 'function' && resolveUserName(message?.senderId)?.name) || message?.username || t("chatPage.comments.messageFallback", "Message")}</div>
        <div className="side-drawer__message-body">
          {typeof messageContentRenderer === "function"
            ? messageContentRenderer(message?.content || "", message)
            : <RichTextRenderer html={message?.content || ""} />}
        </div>
      </div>
      <div className="side-drawer__comments">
        <div className="side-drawer__comments-header">
          <span>{t("chatPage.comments.count", { count: total })}</span>
        </div>
        <div className="side-drawer__comments-list" role="list" aria-busy={loading}>
          {comments.map((c) => {
            const isOwner = currentUserId && String(c.userId) === String(currentUserId);
            const profile = (typeof resolveUserName === 'function' && resolveUserName(c.userId)) || {};
            const author = profile.name || profile.username || t("chatPage.comments.userFallback", "User");
            const avatarUrl = profile.avatarUrl || undefined;
            return (
              <CommentRow
                key={c.id}
                userId={c.userId}
                username={author}
                avatarUrl={avatarUrl}
                createdAt={c.createdAt}
                canEdit={Boolean(isOwner)}
                isEditing={String(editingCommentId) === String(c.id)}
                onEdit={() => setEditingCommentId(c.id)}
                onCancelEdit={() => setEditingCommentId(null)}
                onSaveEdit={(nextHtml) => handleEdit(c.id, nextHtml)}
                onDelete={() => handleDelete(c.id)}
              >
                {c.content}
              </CommentRow>
            );
          })}
          {error && <div className="side-drawer__comments-error" role="alert">{error}</div>}
          {comments.length < total && (
            <div className="side-drawer__comments-more">
              <button type="button" onClick={() => fetchPage(page + 1)} disabled={loading}>
                {loading ? t("chatPage.comments.loading", "Loading…") : t("chatPage.comments.loadMore", "Load more")}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="composer-v2" role="form" aria-label={t("chatPage.comments.composerLabel", "Comment composer")}>
        <FormattingToolbar
          onBold={() => applyWrapFormatting({ kind: 'bold' })}
          onItalic={() => applyWrapFormatting({ kind: 'italic' })}
          onStrike={() => applyWrapFormatting({ kind: 'strike' })}
          onLink={applyLinkFormatting}
          onOrdered={() => applyListFormatting({ ordered: true })}
          onBulleted={() => applyListFormatting({ ordered: false })}
          onQuote={applyQuoteFormatting}
          onCode={applyCodeFormatting}
          isBold={formatState.bold}
          isItalic={formatState.italic}
          isStrike={formatState.strike}
          isLink={formatState.link}
          isOrderedList={formatState.ordered}
          isBulletedList={formatState.bulleted}
          isQuote={formatState.quote}
          isCode={formatState.code}
        />
        <RichTextInput
          ref={editorRef}
          className="composer-v2__field"
          ariaLabel={t("chatPage.comments.writeLabel", "Write a comment")}
          valueHtml={draftHtml}
          onChangeHtml={setDraftHtml}
          onKeyDown={handleKeyDown}
          onKeyUp={recomputeFormatState}
          onFocus={recomputeFormatState}
          onClick={recomputeFormatState}
          placeholder={t("chatPage.comments.placeholder", "Write a comment…")}
        />
        <FooterActions
          formattingActive
          onToggleFormatting={() => {}}
          onInsertMention={() => {}}
          onStartVideo={() => {}}
          onRecordAudio={() => {}}
          onCreateCanvas={() => {}}
          onSend={() => handleSubmitComment(getEditorLiveHtml(editorRef) || draftHtml || "")}
          canSend={canSend}
          isSending={false}
          onSelectEmoji={(emoji) => {
            try { editorRef.current?.exec('text', emoji?.native || ''); } catch (_) {}
          }}
          onSelectImages={handleSelectImages}
        />
      </div>
    </div>
  );

  // Inline mode renders only the inner content (no overlay/backdrop); overlay keeps the drawer UI
  if (variant === "inline") {
    return renderDrawerContent();
  }

  return (
    <div className={classNames("side-drawer", { "side-drawer--open": isOpen })} role="dialog" aria-modal="true" aria-label={t("chatPage.comments.dialogLabel", "Message comments")}>
      <div className="side-drawer__backdrop" onClick={onClose} />
      <div ref={panelRef} className="side-drawer__panel">
        {showHeader && (
          <header className="side-drawer__header">
            <h2 className="side-drawer__title">{t("chatPage.comments.title", "Comments")}</h2>
            <button type="button" className="side-drawer__close" onClick={onClose} aria-label={t("chatPage.comments.close", "Close comments")}>×</button>
          </header>
        )}
        {renderDrawerContent()}
      </div>
    </div>
  );
}

MessageCommentDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chatId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    senderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string,
    username: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  initialDraft: PropTypes.string,
  messageContentRenderer: PropTypes.func,
  stompClient: PropTypes.object,
  isSocketConnected: PropTypes.bool,
  resolveUserName: PropTypes.func,
  variant: PropTypes.oneOf(["overlay", "inline"]),
  showHeader: PropTypes.bool,
};
